import { debug } from 'utils/debug';
import { bootHash } from './bootHash';
import { CodecOptions, ROUTE_PARAM_KEYS, encodeRoute } from './queryRouteCodec';

/** The legacy hashbang prefix the embed used to route under (`#!/e/slug`). */
const HASHBANG_PREFIX = '#!';

/**
 * Parse a legacy hashbang hash (`#!/e/slug?date=…`) into its inner route, or
 * `null` when the hash isn't a hashbang route. Covers every legacy route, not
 * just `/e/`, because it reads the whole inner path.
 */
export const parseLegacyHash = (
  hash: string,
): { pathname: string; search: string } | null => {
  if (!hash.startsWith(`${HASHBANG_PREFIX}/`)) {
    return null;
  }

  const inner = hash.slice(HASHBANG_PREFIX.length);
  const at = inner.indexOf('?');
  if (at === -1) {
    return { pathname: inner, search: '' };
  }

  return { pathname: inner.slice(0, at), search: inner.slice(at) };
};

/**
 * On boot in query mode, upgrade a legacy `#!/…` address to the crawlable query
 * form via `replaceState`, preserving the client's existing params (e.g. `icamp`)
 * and dropping the hash. This keeps published hashbang links (emails, QR codes)
 * working with zero client action, and self-heals: a visitor who re-copies the
 * upgraded URL now shares the crawlable form. No-op when there is no hashbang hash.
 *
 * It restores *functionality* for legacy links; it does not make the old hashbang
 * URLs themselves crawlable (they never were).
 *
 * The query string wins over the hash: if `location.search` ALREADY encodes a route
 * (`?experienceId=`, `?view=`, …), that is the authoritative route and the hash is a
 * stale leftover — so we leave the URL untouched. Without this, a host page that
 * re-stamps `#!/…` onto every URL (some partners wrap the embed in a hash-preserving
 * history shim) would, on every reload, have its stale hash override the real query
 * route and revert the visitor to whatever the hash pointed at.
 */
export const upgradeLegacyHashUrl = ({
  listIsHome = false,
}: CodecOptions = {}): void => {
  // A host script may have rewritten the hash before this ran; the boot-time hash is the link.
  const route =
    parseLegacyHash(window.location.hash) ?? parseLegacyHash(bootHash);
  if (!route) {
    return;
  }

  const currentParams = new URLSearchParams(window.location.search);
  if (ROUTE_PARAM_KEYS.some((key) => currentParams.has(key))) {
    return;
  }

  const search = encodeRoute(route, window.location.search, { listIsHome });
  const upgradedUrl = `${window.location.pathname}${search}`;

  try {
    window.history.replaceState(window.history.state, '', upgradedUrl);
  } catch (error) {
    // `replaceState` throws in opaque-origin contexts (sandboxed iframes without
    // `allow-same-origin`, `about:blank`/`file:`). The upgrade is a best-effort
    // SEO/deep-link nicety, so swallow the failure and let the router boot — the
    // widget still mounts (on the brand home) rather than dying on a blank screen.
    debug(
      'error',
      'legacyHashShim: replaceState failed, skipping URL upgrade',
      error,
    );
  }
};
